/**
 * fast-xml-parser 包裝與弱型別 XML 節點存取 helper。
 *
 * 刻意不使用 DOMParser（core 必須能在 Node 執行），改用 fast-xml-parser。
 * 解析結果一律關閉自動型別推斷（`parseTagValue: false`），型別轉換完全
 * 由本檔案的 helper 明確處理，避免 `any` 與隱性錯誤。
 */

import { XMLParser, XMLBuilder } from 'fast-xml-parser';

/** 弱型別 XML 節點：物件的 key 為子元素／屬性名稱，value 型別不定。 */
export type XmlNode = Record<string, unknown>;

const ATTR_PREFIX = '@_';
const TEXT_NODE_NAME = '#text';

/** 一律以陣列解析的元素路徑（即使只出現一次也要是陣列，方便統一處理）。 */
const ALWAYS_ARRAY_TAGS = new Set([
  'Task',
  'PredecessorLink',
  'Resource',
  'Assignment',
  'Calendar',
  'WeekDay',
  'WorkingTime',
  'TimePeriod',
  'AvailabilityPeriod',
  'Baseline',
  'TimephasedData',
  'ExtendedAttribute',
]);

export function createMspdiParser(): XMLParser {
  return new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: ATTR_PREFIX,
    textNodeName: TEXT_NODE_NAME,
    parseTagValue: false,
    parseAttributeValue: false,
    trimValues: true,
    isArray: (tagName) => ALWAYS_ARRAY_TAGS.has(tagName),
  });
}

export function createMspdiBuilder(): XMLBuilder {
  return new XMLBuilder({
    ignoreAttributes: false,
    attributeNamePrefix: ATTR_PREFIX,
    textNodeName: TEXT_NODE_NAME,
    format: true,
    indentBy: '  ',
    suppressEmptyNode: false,
  });
}

/** 將任意值收斂為陣列（undefined -> []，單一值 -> [值]，陣列 -> 原樣）。 */
export function asArray(value: unknown): readonly unknown[] {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/** 將任意值收斂為 XmlNode（純文字或非物件回傳 undefined）。 */
export function asNode(value: unknown): XmlNode | undefined {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as XmlNode;
  }
  return undefined;
}

/** 取出元素的純文字內容（處理帶屬性時 text 落在 `#text` 的情形）。 */
export function textOf(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  const node = asNode(value);
  if (node && typeof node[TEXT_NODE_NAME] === 'string') {
    return node[TEXT_NODE_NAME] as string;
  }
  return undefined;
}

/** 讀取子元素的文字內容。 */
export function childText(node: XmlNode | undefined, name: string): string | undefined {
  if (!node) {
    return undefined;
  }
  const text = textOf(node[name]);
  return text !== undefined && text.length > 0 ? text : undefined;
}

/** 讀取子元素文字並轉為數字；解析失敗回傳 undefined。 */
export function childNumber(node: XmlNode | undefined, name: string): number | undefined {
  const text = childText(node, name);
  if (text === undefined) {
    return undefined;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** 讀取子元素文字並轉為布林（MSPDI 慣例：`"1"`/`"0"` 或 `"true"`/`"false"`）。 */
export function childBoolean(node: XmlNode | undefined, name: string): boolean | undefined {
  const text = childText(node, name);
  if (text === undefined) {
    return undefined;
  }
  return text === '1' || text.toLowerCase() === 'true';
}

/** 讀取屬性值。 */
export function attr(node: XmlNode | undefined, name: string): string | undefined {
  if (!node) {
    return undefined;
  }
  const value = node[`${ATTR_PREFIX}${name}`];
  return typeof value === 'string' ? value : undefined;
}

export function attrNumber(node: XmlNode | undefined, name: string): number | undefined {
  const text = attr(node, name);
  if (text === undefined) {
    return undefined;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * 解析可能帶貨幣符號／千分位的費率字串（例如 `"NT$1,200.00/hr"`、`"1500"`）。
 * 只取出數字部分；無法解析回傳 undefined。
 */
export function parseCurrencyNumber(value: string | undefined): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  const cleaned = value.replace(/[^0-9.\-]/g, '');
  if (cleaned.length === 0) {
    return undefined;
  }
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** 費率字串是否為「每分鐘」以外的單位（`/h`, `/d`, `/w`, `/mo`, `/y`）；預設每小時。 */
export function rateDivisorPerMinute(value: string | undefined): number {
  if (value === undefined) {
    return 60;
  }
  if (/\/m(in)?\b/i.test(value)) {
    return 1;
  }
  if (/\/d\b/i.test(value)) {
    return 60 * 24;
  }
  if (/\/w\b/i.test(value)) {
    return 60 * 24 * 7;
  }
  if (/\/mo\b/i.test(value)) {
    return 60 * 24 * 30;
  }
  if (/\/y\b/i.test(value)) {
    return 60 * 24 * 365;
  }
  // 預設（含 `/h`）：每小時費率換算為每分鐘。
  return 60;
}
